import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './Signup.css';
import logo from '../../assets/logo.png';
import api from '../../api';
import { GoogleLogin } from '@react-oauth/google';
import { useAuth } from '../../context/AuthContext';

const OTP_LENGTH = 6;

const Signup = () => {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(''));
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(true);
  const [resendCooldown, setResendCooldown] = useState(0);
  const inputRefs = useRef([]);

  // Countdown for the resend OTP cooldown
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => {
      setResendCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const handleCheckboxChange = () => {
    setAgreeToTerms(!agreeToTerms);
  };

  const handleSendOtp = async () => {
    const trimmedEmail = email.trim().toLowerCase();

    if (!trimmedEmail) {
      toast.error('Please enter your email.');
      return;
    }

    if (!agreeToTerms) {
      toast.error('Please accept Terms and Conditions to continue.');
      return;
    }

    try {
      setIsSendingOtp(true);
      await axios.post(api.sendOtp, {
        email: trimmedEmail,
        isSignup: true,
      });

      setIsOtpSent(true);
      setResendCooldown(30);
      toast.success('OTP sent to your email!');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send OTP');
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendCooldown > 0 || isSendingOtp) return;
    setOtp(Array(OTP_LENGTH).fill(''));
    inputRefs.current[0]?.focus();
    await handleSendOtp();
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedOtp = e.clipboardData
      .getData('text')
      .replace(/\D/g, '')
      .slice(0, OTP_LENGTH);

    if (!pastedOtp) return;

    const newOtp = Array(OTP_LENGTH).fill('');
    pastedOtp.split('').forEach((digit, index) => {
      newOtp[index] = digit;
    });
    setOtp(newOtp);

    const nextIndex = Math.min(pastedOtp.length, OTP_LENGTH - 1);
    inputRefs.current[nextIndex]?.focus();
  };

  const handleOtpBackspace = (index, value) => {
    if (!value && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerifyAndRegister = async () => {
    const trimmedEmail = email.trim().toLowerCase();
    const otpValue = otp.join('');

    if (!trimmedEmail) {
      toast.error('Please enter your email.');
      return;
    }

    if (otpValue.length < OTP_LENGTH) {
      toast.error('Please enter the 6-digit OTP.');
      return;
    }

    if (!agreeToTerms) {
      toast.error('Please accept Terms and Conditions to continue.');
      return;
    }

    try {
      setIsVerifyingOtp(true);

      await axios.post(api.verifyOtp, {
        email: trimmedEmail,
        otp: otpValue,
      });

      const formDataToSend = new FormData();
      formDataToSend.append('email', trimmedEmail);

      const response = await axios.post(api.signUp, formDataToSend, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const { token, refreshToken, userId } = response.data;
      login(token, refreshToken);
      localStorage.setItem('authToken', token);

      if (refreshToken) {
        localStorage.setItem('refreshToken', refreshToken);
      }

      localStorage.setItem('tempUserId', userId);
      toast.success('Registration successful!');
      navigate('/profile-setup');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Invalid OTP or registration failed.');
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    if (!agreeToTerms) {
      toast.error('Please accept Terms and Conditions to continue.');
      return;
    }

    try {
      const response = await axios.post(api.googleLogin, {
        credential: credentialResponse.credential,
      });

      const { token, refreshToken, redirectTo, redirectToRoleSelection, roles, isNewUser } = response.data;

      login(token, refreshToken);
      localStorage.setItem('authToken', token);
      if (refreshToken) {
        localStorage.setItem('refreshToken', refreshToken);
      }

      if (redirectToRoleSelection) {
        toast.info('Multiple roles found. Please select one.');
        navigate('/role-selection', { state: { roles } });
        return;
      }

      toast.success(isNewUser ? 'Welcome to UlsaApp!' : 'Google signup successful!');
      navigate(redirectTo);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Google signup failed. Please try again.');
    }
  };

  const handleGoogleError = () => {
    toast.error('Google signup failed. Please try again.');
  };

  return (
    <div className="signup-bg">
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />

      <div className="signup-auth-container">
        <div className="signup-welcome-message">
          <img src={logo} alt="Platform Logo" className="signup-logo-image" />
          <h2>Welcome to UlsaApp!</h2>
          <p className="signup-para">
            Empower minds, inspire change! Join our UlsaApp as an instructor or student. Share your expertise, unlock new opportunities, and shape the future.</p>
          {/* <p className="signup-para-bottom">
            Start your account with Google or OTP. You can complete profile details right after signup.
          </p> */}
          <p className="journey">Already have an account?</p>
          <button
            className="signup-rg-button"
            onClick={() => (window.location.href = '/login')}
          >
            Login
          </button>
        </div>

        <form className="signup-auth-form" onSubmit={(e) => e.preventDefault()}>
          <h2>Register</h2>
          <div className="signup-google-container">
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              text="continue_with"
              shape="rectangular"
              size="large"
              width="100%"
              theme="outline"
            />
          </div>

          <div className="signup-divider">
            <span>OR</span>
          </div>
          
          <input
            type="email"
            name="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            required
            className="signup-email"
          />

          {!isOtpSent ? (
            <div className="signup-button-container signup-button-center">
              <button
                type="button"
                className="signup-next-button otp-primary-button"
                onClick={handleSendOtp}
                disabled={isSendingOtp}
              >
                {isSendingOtp ? 'Sending OTP...' : 'Send OTP'}
              </button>
            </div>
          ) : (
            <>
              <div className="otp-input-container">
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    type="text"
                    className="otp-box"
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onPaste={handleOtpPaste}
                    onKeyDown={(e) => {
                      if (e.key === 'Backspace') {
                        handleOtpBackspace(index, e.target.value);
                      }
                    }}
                    maxLength="1"
                    ref={(el) => (inputRefs.current[index] = el)}
                    inputMode="numeric"
                  />
                ))}
              </div>

              <div className="signup-button-container signup-button-center">
                <button
                  type="button"
                  className="signup-next-button otp-primary-button"
                  onClick={handleVerifyAndRegister}
                  disabled={isVerifyingOtp || otp.some((d) => d === '')}
                >
                  {isVerifyingOtp ? 'Verifying...' : 'Verify OTP & Continue'}
                </button>
              </div>

              <p className="otp-resend-text">
                Didn&apos;t receive the code?{' '}
                <button
                  type="button"
                  className="otp-resend-button"
                  onClick={handleResendOtp}
                  disabled={resendCooldown > 0 || isSendingOtp}
                >
                  {isSendingOtp
                    ? 'Sending...'
                    : resendCooldown > 0
                      ? `Resend OTP in ${resendCooldown}s`
                      : 'Resend OTP'}
                </button>
              </p>
            </>
          )}
          <div className="terms-container">
            <input
              type="checkbox"
              id="terms"
              checked={agreeToTerms}
              onChange={handleCheckboxChange}
            />
            <label htmlFor="terms" className="terms-label">
              I agree to the{' '}
              <a href="/terms-and-conditions" target="_blank" rel="noopener noreferrer">
                Terms of Service
              </a>{' '}and{' '}
              <a href="/privacy" target="_blank" rel="noopener noreferrer">
                Privacy Policy
              </a>.
            </label>
          </div>

          <p className="signup-email-notice">
            <strong>Note:</strong> Please enter a valid email address, OTP will be sent to this email.
          </p>
        </form>
      </div>
    </div>
  );
};

export default Signup;
